import { type BadgeTone, badgeToneClass } from "components/ui/StatusBadge";
import { useMemo } from "react";
import type { BadgeAuditScreen } from "routes/SnippetsPage/badgeAudit";
import {
  badgeAuditProposal,
  type ProposalScreen,
} from "routes/SnippetsPage/badgeAuditProposal";

const GROUP_LABELS: Record<BadgeAuditScreen["group"], string> = {
  delivery: "メール一斉配信",
  matching: "マッチング",
  crm: "CRM",
  settings: "設定",
  admin: "管理画面",
  email: "メール詳細・検索",
  home: "ホーム/プロフィール",
};

const GROUP_ORDER: BadgeAuditScreen["group"][] = [
  "delivery",
  "matching",
  "crm",
  "settings",
  "admin",
  "email",
  "home",
];

/**
 * 現状の badge / status / tag audit。
 * 各画面で使われている全 badge を網羅的に並べ、color の使用頻度を集計する。
 * 理想形 (BadgeAuditProposalView) と同じデータソースを参照しているため、
 * 全画面の置換完了後は理想形と一致した状態になる。
 * @return {JSX.Element} audit セクション
 */
export const BadgeAuditView = (): JSX.Element => {
  const screensByGroup = useMemo(() => {
    const map: Record<string, ProposalScreen[]> = {};
    for (const s of badgeAuditProposal) {
      if (!map[s.group]) map[s.group] = [];
      map[s.group].push(s);
    }
    return map;
  }, []);

  // 同じ tone を使っている entry の集計
  const toneUsage = useMemo(() => {
    const map = new Map<BadgeTone, { count: number; labels: Set<string> }>();
    for (const screen of badgeAuditProposal) {
      for (const entry of screen.entries) {
        const v = map.get(entry.tone) ?? {
          count: 0,
          labels: new Set<string>(),
        };
        v.count += 1;
        v.labels.add(entry.label);
        map.set(entry.tone, v);
      }
    }
    return Array.from(map.entries())
      .map(([tone, v]) => ({
        tone,
        count: v.count,
        labels: Array.from(v.labels),
      }))
      .sort((a, b) => b.count - a.count);
  }, []);

  return (
    <div className="space-y-10">
      <header className="space-y-2">
        <h2 className="text-xl font-bold">Status / Tag / Badge Audit</h2>
        <p className="text-sm text-base-content/70">
          全画面で使われている badge / status / tag のラベルと色を網羅的に並べた
          audit。 理想形 (Proposal タブ) を全画面に適用したあとの状態。
        </p>
        <div className="text-xs text-base-content/60 flex gap-4">
          <span>Screens: {badgeAuditProposal.length}</span>
          <span>
            Entries:{" "}
            {badgeAuditProposal.reduce((s, x) => s + x.entries.length, 0)}
          </span>
          <span>Unique tones: {toneUsage.length}</span>
        </div>
      </header>

      {/* tone 使用頻度 */}
      <section className="space-y-3">
        <h3 className="text-base font-semibold">tone 使用頻度ランキング</h3>
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          <table className="table table-xs w-full">
            <thead className="bg-primary text-white">
              <tr>
                <th>サンプル</th>
                <th>tone</th>
                <th>使用回数</th>
                <th>ユニークなラベル</th>
              </tr>
            </thead>
            <tbody>
              {toneUsage.map(row => (
                <tr key={row.tone}>
                  <td>
                    <span className={badgeToneClass[row.tone]}>sample</span>
                  </td>
                  <td>
                    <code className="text-[10px] bg-base-200 px-1 rounded">
                      {row.tone}
                    </code>
                  </td>
                  <td>{row.count}</td>
                  <td className="text-[11px] text-base-content/70">
                    {row.labels.join(" / ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* グループごとに画面を並べる */}
      {GROUP_ORDER.map(group => {
        const screens = screensByGroup[group];
        if (!screens || screens.length === 0) return null;
        return (
          <section key={group} className="space-y-4">
            <h3 className="text-base font-semibold border-b border-slate-200 pb-1">
              {GROUP_LABELS[group]}
            </h3>
            {screens.map(s => (
              <div
                key={s.screen}
                className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
              >
                <p className="text-xs font-semibold text-base-content/70 mb-2">
                  {s.screen}
                </p>
                <div className="flex flex-wrap gap-2">
                  {s.entries.map((e, idx) => (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: audit data is static
                      key={`${e.label}-${idx}`}
                      className="flex items-center gap-1.5 border border-slate-200 rounded px-2 py-1"
                    >
                      <span className={badgeToneClass[e.tone]}>{e.label}</span>
                      <code className="text-[10px] text-base-content/50">
                        {e.tone}
                      </code>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </section>
        );
      })}
    </div>
  );
};
